import { DatasetSummary, DebateProposal } from "./types";

const PROMPT_TOP_PRODUCT_LIMIT_MIN = 28;
const PROMPT_TOP_PRODUCT_LIMIT_MAX = 44;
const PROMPT_REFERENCE_LIMIT_MIN = 15;
const PROMPT_REFERENCE_LIMIT_MAX = 30;
const PROMPT_KEYWORD_LIMIT = 12;
const PROMPT_MARGIN_LIMIT = 12;
const PROMPT_FINAL_TOP_PRODUCT_LIMIT = 18;
const PROMPT_FINAL_REFERENCE_LIMIT = 12;
const PROMPT_FINAL_KEYWORD_LIMIT = 8;
const PROMPT_REASON_MAX_CHARS = 140;
const PROMPT_OPINION_MAX_CHARS = 320;
const PROMPT_ASSUMPTION_LIMIT = 4;
const PROMPT_COMPANY_LIMIT = 4;

const compactPromptText = (value: string, maxChars: number) => {
  const normalized = String(value || "").replace(/\s+/g, " ").trim();
  if (normalized.length <= maxChars) return normalized;
  return `${normalized.slice(0, Math.max(0, maxChars - 3)).trim()}...`;
};

const compactPromptObject = <T extends Record<string, any>>(value: T) =>
  Object.fromEntries(
    Object.entries(value).filter(
      ([, entry]) =>
        entry !== null &&
        entry !== undefined &&
        entry !== "" &&
        (!Array.isArray(entry) || entry.length > 0),
    ),
  );

const buildPromptDatasetSummary = (
  dataset: DatasetSummary,
  maxItemCount: number,
) => {
  const topProductLimit = Math.max(
    PROMPT_TOP_PRODUCT_LIMIT_MIN,
    Math.min(maxItemCount + 8, PROMPT_TOP_PRODUCT_LIMIT_MAX),
  );
  const referenceLimit = Math.max(
    PROMPT_REFERENCE_LIMIT_MIN,
    Math.min(maxItemCount, PROMPT_REFERENCE_LIMIT_MAX),
  );

  return compactPromptObject({
    affiliation: dataset.affiliation,
    period_mode: dataset.period_mode,
    period_start: dataset.period_start,
    period_end: dataset.period_end,
    source_rows: dataset.source_rows,
    source_receipts: dataset.source_receipts,
    source_total_qty: dataset.source_total_qty,
    source_total_sales: dataset.source_total_sales,
    inbound_rows: dataset.inbound_rows,
    outbound_rows: dataset.outbound_rows,
    request_rows: dataset.request_rows,
    request_matched_items: dataset.request_matched_items,
    markup_rows: dataset.markup_rows,
    remaining_stock_items: dataset.remaining_stock_items,
    remaining_stock_total_qty: dataset.remaining_stock_total_qty,
    top_products: dataset.top_products.slice(0, topProductLimit).map((product) =>
      compactPromptObject({
        product_name: product.product_name,
        category_hint: product.category_hint,
        total_quantity: product.total_quantity,
        total_sales: product.total_sales,
        avg_unit_price: product.avg_unit_price,
        order_count: product.order_count,
        inbound_quantity: product.inbound_quantity,
        outbound_quantity: product.outbound_quantity,
        remaining_stock_quantity: product.remaining_stock_quantity,
        outbound_customer_count: product.outbound_customer_count,
        request_count: product.request_count,
        request_company_count: product.request_company_count,
        markup_percent: product.markup_percent,
        curation_priority_score: product.curation_priority_score,
      }),
    ),
    requested_keywords: dataset.requested_keywords
      .slice(0, PROMPT_KEYWORD_LIMIT)
      .map((keyword) =>
        compactPromptObject({
          keyword: keyword.keyword,
          count: keyword.count,
          companies: keyword.companies.slice(0, PROMPT_COMPANY_LIMIT),
        }),
      ),
    high_margin_products: dataset.high_margin_products
      .slice(0, PROMPT_MARGIN_LIMIT)
      .map((product) =>
        compactPromptObject({
          product_name: product.product_name,
          markup_percent: product.markup_percent,
          cost_price: product.cost_price,
          sale_price: product.sale_price,
          coupang_lowest_price: product.coupang_lowest_price,
        }),
      ),
    coupang_references: dataset.coupang_references
      .slice(0, referenceLimit)
      .map((reference) =>
        compactPromptObject({
          product_name: reference.product_name,
          source_label: reference.source_label,
          median_price: reference.median_price,
          search_url: reference.search_url,
        }),
      ),
  });
};

const buildFinalPromptDatasetSummary = (dataset: DatasetSummary) =>
  compactPromptObject({
    affiliation: dataset.affiliation,
    period_mode: dataset.period_mode,
    period_start: dataset.period_start,
    period_end: dataset.period_end,
    remaining_stock_items: dataset.remaining_stock_items,
    remaining_stock_total_qty: dataset.remaining_stock_total_qty,
    top_products: dataset.top_products
      .slice(0, PROMPT_FINAL_TOP_PRODUCT_LIMIT)
      .map((product) =>
        compactPromptObject({
          product_name: product.product_name,
          category_hint: product.category_hint,
          total_quantity: product.total_quantity,
          avg_unit_price: product.avg_unit_price,
          outbound_quantity: product.outbound_quantity,
          request_count: product.request_count,
          remaining_stock_quantity: product.remaining_stock_quantity,
          curation_priority_score: product.curation_priority_score,
        }),
      ),
    requested_keywords: dataset.requested_keywords
      .slice(0, PROMPT_FINAL_KEYWORD_LIMIT)
      .map((keyword) =>
        compactPromptObject({
          keyword: keyword.keyword,
          count: keyword.count,
        }),
      ),
    coupang_references: dataset.coupang_references
      .slice(0, PROMPT_FINAL_REFERENCE_LIMIT)
      .map((reference) =>
        compactPromptObject({
          product_name: reference.product_name,
          source_label: reference.source_label,
          median_price: reference.median_price,
        }),
      ),
  });

const buildPromptProposalSummary = (
  proposal: DebateProposal,
  options: { includeReasons?: boolean; includeTotalPrice?: boolean } = {},
) =>
  compactPromptObject({
    assistant: proposal.assistant,
    opinion: compactPromptText(proposal.opinion, PROMPT_OPINION_MAX_CHARS),
    assumptions: proposal.assumptions
      .map((assumption) =>
        compactPromptText(assumption, PROMPT_REASON_MAX_CHARS),
      )
      .filter(Boolean)
      .slice(0, PROMPT_ASSUMPTION_LIMIT),
    target_budget: proposal.target_budget,
    estimated_total_cost: proposal.estimated_total_cost,
    snack_beverage_ratio: proposal.snack_beverage_ratio,
    items: proposal.items.map((item) =>
      compactPromptObject({
        product_name: item.product_name,
        category: item.category,
        quantity: item.quantity,
        unit_price: item.unit_price,
        ...(options.includeTotalPrice !== false
          ? { total_price: item.total_price }
          : {}),
        reference_source: item.reference_source,
        ...(options.includeReasons
          ? { reason: compactPromptText(item.reason, PROMPT_REASON_MAX_CHARS) }
          : {}),
      }),
    ),
  });

const stringifyPromptJson = (value: unknown) => JSON.stringify(value);

const buildReasonRuleGuide = (customerFacing = false) => {
  if (customerFacing) {
    return `
- reason에는 판매데이터 근거(판매수량/매출/인기 등)와 외부참조 근거(가격/검색결과)를 함께 쓰고, 가능하면 출고/간식신청 신호까지 반영할 것.
- request_count, outbound_quantity가 강한 품목은 reason에 그 숫자 또는 의미를 직접 언급할 것.
- markup_percent는 품목 선정의 내부 참고 신호로만 사용하고, reason에는 마진/마진율/수익성/이윤 관련 표현을 절대 쓰지 말 것.
- 입출고 기준 잔여수량은 추천 우선순위에 활용하되, reason에는 "재고 우선", "재고 소진", "창고 재고" 같은 운영 문구를 쓰지 말 것.
`;
  }

  return `
- reason에는 판매데이터 근거(판매수량/매출/인기 등)와 외부참조 근거(가격/검색결과)를 함께 쓰고, 가능하면 출고/간식신청/마진 신호까지 반영할 것.
- request_count, outbound_quantity, markup_percent가 강한 품목은 reason에 그 숫자 또는 의미를 직접 언급할 것.
- 입출고 기준 잔여수량은 추천 우선순위에 활용하되, reason에는 "재고 우선", "재고 소진", "창고 재고" 같은 운영 문구를 쓰지 말 것.
`;
};

const buildResponseFormatGuide = (
  targetItemCount: number,
  maxItemCount: number,
  customerFacing = false,
) => `
반드시 JSON 객체 하나만 출력하세요. Markdown, 코드블록, 설명문 금지.
필수 스키마:
{
  "assistant": "chatgpt 또는 gemini",
  "opinion": "요약 의견 문자열",
  "assumptions": ["가정1", "가정2"],
  "target_budget": 숫자 또는 null,
  "estimated_total_cost": 숫자,
  "snack_beverage_ratio": "예: 6:4",
  "items": [
    {
      "product_name": "품목명",
      "category": "snack 또는 beverage",
      "quantity": 숫자,
      "unit_price": 숫자,
      "total_price": 숫자,
      "reference_source": "쿠팡|네이버스토어|마켓컬리|11번가|G마켓|판매데이터",
      "coupang_reference_price": 숫자,
      "coupang_search_url": "외부 검색 URL",
      "reason": "추천 근거"
    }
  ]
}
규칙:
- items는 최소 ${targetItemCount}개, 최대 ${maxItemCount}개로 구성.
- 사용자 요구에 품목 수가 명시되면 그 개수를 정확히 맞출 것.
- 같은 유형만 반복하지 말고 판매 상위 품목을 폭넓게 포함해 다양하게 구성.
- opinion은 2~3문장 이내로 간결하게 작성.
- assumptions는 최대 4개까지만 작성.
- quantity, unit_price, total_price는 0보다 커야 함.
- total_price = quantity * unit_price를 최대한 맞출 것.
- target_budget이 요구사항에 있으면 items 합계(estimated_total_cost)는 target_budget을 초과하지 말 것.
- dataset.coupang_references(외부참조: 쿠팡/네이버스토어/마켓컬리/11번가/G마켓)가 있으면 해당 품목/단가를 우선 사용.
- dataset.coupang_references가 비어있으면 판매데이터 단가 기준 추정 허용.
- unit_price는 coupang_reference_price와 동일하게 둘 것.
- coupang_search_url은 coupang.com / naver.com / kurly.com / 11st.co.kr / gmarket.co.kr 중 하나를 사용.
- reason은 품목당 1~2문장, 110자 내외로 간결하게 작성.
${buildReasonRuleGuide(customerFacing)}
- 요구사항의 금지 표현(예: 제외/금지/절대/말자)은 절대 위반하지 말 것.
`;

const buildCoupangConditionGuide = (dataset: DatasetSummary) => {
  if (dataset.coupang_references.length > 0) {
    return `외부 참조 ${dataset.coupang_references.length}건(쿠팡/네이버스토어/마켓컬리/11번가/G마켓)이 제공됨. 가능한 한 이 참조를 직접 사용하세요.`;
  }
  return "외부 참조 데이터가 비어있음. 이번 실행에서는 판매데이터만 기반으로 추천하고, coupang_search_url은 해당 품목 검색 URL로 채우세요.";
};

const buildRemainingStockGuide = (dataset: DatasetSummary) => {
  if (dataset.remaining_stock_items <= 0) {
    return "입출고 기준 잔여수량 데이터가 충분하지 않으므로 판매/출고/신청/마진 중심으로 추천하세요.";
  }

  return `
입출고 기반 잔여수량 규칙:
- dataset.top_products[].remaining_stock_quantity = 입고수량 - 출고수량으로 계산한 잔여수량.
- remaining_stock_quantity가 큰 품목은 운영 가능성이 높은 후보로 참고할 것.
- 단, 추천 사유(reason)에는 "재고 우선", "재고 소진", "창고 재고" 같은 내부 운영 표현을 쓰지 말 것.
- 잔여수량은 품목 선정의 내부 판단 신호로만 사용하고, reason은 판매/출고/신청/마진/가격 근거 중심으로 작성할 것.
`;
};

const buildSupplementalSignalGuide = (
  dataset: DatasetSummary,
  customerFacing = false,
) => {
  if (
    dataset.inbound_rows <= 0 &&
    dataset.outbound_rows <= 0 &&
    dataset.request_rows <= 0 &&
    dataset.markup_rows <= 0
  ) {
    return "입고/출고/간식신청서/마진 데이터가 비어 있으므로 판매데이터 중심으로 추천하세요.";
  }

  return `
보조 데이터 해석 규칙:
- dataset.top_products[].outbound_quantity, outbound_customer_count: 실제 고객사 출고 실적과 확산도. 높을수록 검증된 수요로 해석.
- dataset.top_products[].inbound_quantity: 입고량. 반복 운영 가능한 품목으로 해석.
- dataset.top_products[].remaining_stock_quantity: 입출고 기준 잔여수량. 품목 선정 내부 판단에 참고.
- dataset.top_products[].request_count, request_company_count: 간식신청서 선호도. 높을수록 고객 선호가 뚜렷함.
- dataset.top_products[].markup_percent: ${
    customerFacing
      ? "내부 우선순위 참고용 지표. reason에는 노출하지 말 것."
      : "마진율. 높을수록 수익성이 좋은 품목."
  }
- dataset.top_products[].curation_priority_score: 판매 + 입출고 잔여수량 + 출고 + 신청 + 마진을 합친 종합 우선순위 점수.
- dataset.high_margin_products: 마진이 큰 품목 후보.
- dataset.requested_keywords: 신청서에서 자주 나온 요청 품목.
적용 규칙:
- 판매 상위 품목만 반복하지 말고, request_count 또는 outbound_quantity가 높은 품목을 적극 포함할 것.
- remaining_stock_quantity가 큰 품목은 동일 조건이면 우선 검토하되, 사유에서는 내부 재고 운영 문구를 감출 것.
- ${
    customerFacing
      ? "markup_percent는 내부 검토에는 참고 가능하지만, reason에는 마진/수익성 표현을 쓰지 말 것."
      : "markup_percent가 높은 품목은 예산/구성 조건을 해치지 않는 범위에서 우선 검토할 것."
  }
- reason에는 사용한 보조 데이터 신호를 구체적으로 반영할 것.
`;
};

export const buildOpenAIDraftPrompt = (
  requirement: string,
  dataset: DatasetSummary,
  targetItemCount: number,
  maxItemCount: number,
  customerFacing = false,
) => `
사용자 자연어 요구:
${requirement || "(추가 요구 없음: 판매데이터 기반으로 균형 추천)"}

큐레이션 입력 데이터 요약(JSON, 용량을 줄이기 위해 상위 후보와 핵심 필드만 남긴 축약본):
${stringifyPromptJson(buildPromptDatasetSummary(dataset, maxItemCount))}

요청:
1) 데이터 기반으로 추천안 작성
2) 사용자의 예산/비율 조건 반영
3) 근거를 항목별 reason에 구체적으로 작성
4) ${buildCoupangConditionGuide(dataset)}
5) ${buildRemainingStockGuide(dataset)}
6) ${buildSupplementalSignalGuide(dataset, customerFacing)}
${buildResponseFormatGuide(targetItemCount, maxItemCount, customerFacing)}
`;

export const buildGeminiRevisionPrompt = (
  requirement: string,
  dataset: DatasetSummary,
  chatgptDraft: DebateProposal,
  targetItemCount: number,
  maxItemCount: number,
  customerFacing = false,
) => `
사용자 자연어 요구:
${requirement || "(추가 요구 없음: 판매데이터 기반으로 균형 추천)"}

큐레이션 입력 데이터 요약(JSON, 용량을 줄이기 위해 상위 후보와 핵심 필드만 남긴 축약본):
${stringifyPromptJson(buildPromptDatasetSummary(dataset, maxItemCount))}

ChatGPT 초안 요약(JSON):
${stringifyPromptJson(
  buildPromptProposalSummary(chatgptDraft, { includeReasons: true }),
)}

요청:
1) ChatGPT 초안을 비판적으로 검토
2) 예산/비율/판매근거 위반사항이 있으면 수정
3) 수정안을 JSON으로 다시 제시
4) 외부참조는 쿠팡/네이버스토어/마켓컬리/11번가/G마켓만 허용
5) ${buildCoupangConditionGuide(dataset)}
6) ${buildRemainingStockGuide(dataset)}
7) ${buildSupplementalSignalGuide(dataset, customerFacing)}
${buildResponseFormatGuide(targetItemCount, maxItemCount, customerFacing)}
`;

export const buildFinalConsensusPrompt = (
  requirement: string,
  dataset: DatasetSummary,
  chatgptDraft: DebateProposal,
  geminiRevision: DebateProposal,
  targetItemCount: number,
  maxItemCount: number,
  customerFacing = false,
) => `
사용자 자연어 요구:
${requirement || "(추가 요구 없음: 판매데이터 기반으로 균형 추천)"}

최종 합의안용 입력 데이터 요약(JSON, 최종 단계라 최소 필드만 남긴 축약본):
${stringifyPromptJson(buildFinalPromptDatasetSummary(dataset))}

ChatGPT 초안 요약(JSON):
${stringifyPromptJson(
  buildPromptProposalSummary(chatgptDraft, {
    includeReasons: false,
    includeTotalPrice: false,
  }),
)}

Gemini 수정안 요약(JSON):
${stringifyPromptJson(
  buildPromptProposalSummary(geminiRevision, {
    includeReasons: false,
    includeTotalPrice: false,
  }),
)}

요청:
1) 두 안을 통합해 최종 합의안 작성
2) discussion_summary는 1~2문장으로 아주 짧게 작성하고 줄바꿈을 넣지 말 것
3) 외부참조는 쿠팡/네이버스토어/마켓컬리/11번가/G마켓만 허용
4) ${buildCoupangConditionGuide(dataset)}
5) ${buildRemainingStockGuide(dataset)}
6) ${buildSupplementalSignalGuide(dataset, customerFacing)}
7) 품목은 최소 ${targetItemCount}개, 최대 ${maxItemCount}개. 다양성을 최우선으로 구성.
8) opinion은 1문장, assumptions는 최대 3개로 매우 짧게 작성
9) 최종 단계는 토큰 절감을 위해 최소 스키마만 출력. estimated_total_cost, snack_beverage_ratio, total_price, coupang_reference_price, coupang_search_url, reason는 절대 출력하지 말 것
10) items 내부에는 product_name, category, quantity, unit_price만 넣을 것
11) 반드시 JSON 객체 하나만 출력하세요. Markdown, 코드블록, 설명문 금지. 문자열 값 안에 줄바꿈 금지.
12) 아래 스키마를 정확히 따르세요
{
  "assistant": "chatgpt",
  "discussion_summary": "문자열",
  "opinion": "문자열",
  "assumptions": ["가정1", "가정2"],
  "target_budget": 숫자 또는 null,
  "items": [
    {
      "product_name": "품목명",
      "category": "snack 또는 beverage",
      "quantity": 숫자,
      "unit_price": 숫자
    }
  ]
}
`;
